<template>
  <b-card class="mt-3 results-import-panel">
    <h6 class="mb-2">
      Импорт результатов из файла
    </h6>
    <p class="text-muted small mb-3">
      Загрузка напрямую в таблицу результатов (без хронометража). Формат: лист <code>results</code>,
      колонки <code>userId</code>, <code>num</code>, <code>status</code>, <code>resultMs</code>, <code>chipTimeMs</code>
      и <code>cp_&lt;point&gt;_timeMs</code> / <code>cp_&lt;point&gt;_splitMs</code>.
    </p>

    <b-row>
      <b-col
        md="5"
        class="mb-2"
      >
        <b-form-group
          label="Файл (.xlsx)"
          label-size="sm"
        >
          <b-form-file
            v-model="selectedFile"
            accept=".xlsx,.xls"
            :disabled="busy || disabled"
            browse-text="Выбрать"
            placeholder="Файл не выбран"
            size="sm"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="4"
        class="mb-2"
      >
        <b-form-group
          label="Режим"
          label-size="sm"
        >
          <b-form-select
            v-model="importMode"
            :options="modeOptions"
            :disabled="busy || disabled"
            size="sm"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="3"
        class="d-flex align-items-end mb-2"
      >
        <b-button
          variant="outline-secondary"
          size="sm"
          class="mr-2"
          :disabled="!canDownloadTemplate"
          @click="downloadTemplate"
        >
          <b-spinner
            v-if="isDownloadingTemplate"
            small
            class="mr-1"
          />
          Скачать шаблон
        </b-button>
        <b-button
          variant="outline-primary"
          size="sm"
          class="mr-2"
          :disabled="!canDryRun"
          @click="runDryRun"
        >
          <b-spinner
            v-if="isDryRunning"
            small
            class="mr-1"
          />
          Проверка (dry-run)
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!canApply"
          @click="confirmApply"
        >
          <b-spinner
            v-if="isApplying"
            small
            class="mr-1"
          />
          Применить
        </b-button>
      </b-col>
    </b-row>

    <b-alert
      v-if="dryRunReport"
      :variant="dryRunReport.status === 'dry_run_ok' ? 'success' : 'warning'"
      show
      class="mb-0"
    >
      <div><strong>Статус:</strong> {{ dryRunReport.status }}</div>
      <div><strong>Строк в файле:</strong> {{ dryRunReport.rowsRead }}</div>
      <div><strong>Валидных:</strong> {{ dryRunReport.validRows }}</div>
      <div><strong>Будет обновлено:</strong> {{ dryRunReport.upsertCandidates }}</div>
      <div><strong>Будет удалено (replace):</strong> {{ dryRunReport.deleteCandidates }}</div>
      <div
        v-if="dryRunReport.errors && dryRunReport.errors.length"
        class="mt-2"
      >
        <strong>Ошибки:</strong>
        <ul class="mb-0 pl-3 small">
          <li
            v-for="(err, idx) in dryRunReport.errors.slice(0, 20)"
            :key="`err-${idx}`"
          >
            строка {{ err.row }}: [{{ err.code }}] {{ err.message }}
          </li>
        </ul>
        <small
          v-if="dryRunReport.errors.length > 20"
          class="text-muted"
        >…и ещё {{ dryRunReport.errors.length - 20 }}</small>
      </div>
    </b-alert>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormFile,
  BFormSelect,
  BButton,
  BSpinner,
  BAlert,
} from 'bootstrap-vue'
import RaceAnalysisApiService from '@/services/race-analysis-api.service'
import RaceErrorHandler from '@/mixins/race-error-handler'

export default {
  name: 'ResultsImportPanel',
  mixins: [RaceErrorHandler],
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormFile,
    BFormSelect,
    BButton,
    BSpinner,
    BAlert,
  },
  props: {
    startId: {
      type: [Number, String],
      required: true,
    },
    distanceId: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedFile: null,
      importMode: 'replace',
      modeOptions: [
        { value: 'replace', text: 'Полная замена (replace)' },
        { value: 'merge', text: 'Слияние (merge) — скоро', disabled: true },
      ],
      idempotencyKey: null,
      dryRunReport: null,
      isDryRunning: false,
      isApplying: false,
      isDownloadingTemplate: false,
    }
  },
  computed: {
    busy() {
      return this.isDryRunning || this.isApplying || this.isDownloadingTemplate
    },
    canDownloadTemplate() {
      return !!this.distanceId && !this.busy && !this.disabled
    },
    canDryRun() {
      return !!this.selectedFile && !!this.distanceId && !this.busy && !this.disabled
    },
    canApply() {
      return (
        this.dryRunReport?.status === 'dry_run_ok'
        && this.dryRunReport?.importRunId
        && !this.busy
        && !this.disabled
      )
    },
  },
  watch: {
    distanceId() {
      this.resetState()
    },
    selectedFile() {
      this.dryRunReport = null
      this.idempotencyKey = null
    },
  },
  methods: {
    resetState() {
      this.selectedFile = null
      this.dryRunReport = null
      this.idempotencyKey = null
    },
    newIdempotencyKey() {
      return `import-${this.startId}-${this.distanceId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    },
    async runDryRun() {
      if (!this.canDryRun) return
      this.isDryRunning = true
      this.idempotencyKey = this.newIdempotencyKey()
      try {
        const file = this.selectedFile
        this.dryRunReport = await RaceAnalysisApiService.dryRunResultsImport(
          this.startId,
          this.distanceId,
          {
            file,
            mode: this.importMode,
            idempotencyKey: this.idempotencyKey,
          },
        )
      } catch (err) {
        this.dryRunReport = null
        this.handleRaceError(err, 'Не удалось выполнить проверку импорта')
      } finally {
        this.isDryRunning = false
      }
    },
    async downloadTemplate() {
      if (!this.canDownloadTemplate) return
      this.isDownloadingTemplate = true
      try {
        const response = await RaceAnalysisApiService.downloadResultsImportTemplate(
          this.startId,
          this.distanceId,
        )
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        })
        const link = document.createElement('a')
        const url = window.URL.createObjectURL(blob)
        link.href = url
        link.download = `race-results-template-start-${this.startId}-distance-${this.distanceId}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.handleRaceError(err, 'Не удалось скачать шаблон импорта')
      } finally {
        this.isDownloadingTemplate = false
      }
    },
    async confirmApply() {
      if (!this.canApply) return
      const deleteCount = this.dryRunReport.deleteCandidates || 0
      const msg = this.importMode === 'replace'
        ? `Будет записано ${this.dryRunReport.validRows} участников. Удалено строк в дистанции: ${deleteCount}. Продолжить?`
        : `Будет записано ${this.dryRunReport.validRows} участников. Продолжить?`
      const confirmed = await this.$bvModal.msgBoxConfirm(msg, {
        title: 'Импорт результатов',
        okTitle: 'Продолжить',
        cancelTitle: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
      this.runApply()
    },
    async runApply() {
      this.isApplying = true
      try {
        const result = await RaceAnalysisApiService.applyResultsImport(
          this.startId,
          this.distanceId,
          {
            importRunId: this.dryRunReport.importRunId,
            idempotencyKey: this.idempotencyKey,
          },
        )
        const message = `Импорт применён: создано ${result.created}, обновлено ${result.updated}, удалено ${result.deleted}`
        this.$toast?.success?.(message)
        this.$emit('import-applied', result)
        this.resetState()
      } catch (err) {
        this.handleRaceError(err, 'Не удалось применить импорт')
      } finally {
        this.isApplying = false
      }
    },
  },
}
</script>
